import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generateJobDescription } from './api';
import { optionalQuestionsForPositionType } from './questions';
import type {
  Answers,
  GenerateError,
  GenerateRequest,
  GenerateSuccess,
  JobFields,
  Tone,
  BenefitKey,
} from './types';
import type { DideWriterEndpointBase } from './endpoints';

export type WizardPhase = 'wizard' | 'loading' | 'preview' | 'error';

export interface UseDideWriterOptions {
  practiceId?: number;
  jobTypeId?: number | string;
  positionType?: string;
  skillIds?: number[];
  yearsExperience?: string;
  // Current job form values. Sent with every request so the writer works from
  // what the employer has entered, saved or not.
  jobFields?: JobFields;
  initialAnswers?: Answers;
  endpointBase?: DideWriterEndpointBase;
}

const DEFAULT_ANSWERS: Answers = { q4: 'professional' };

export function useDideWriter(opts: UseDideWriterOptions) {
  const [phase, setPhase] = useState<WizardPhase>('wizard');
  // The first screen is the extra-info box and tone. The step-by-step
  // questions are optional extras the user can open from there.
  const [showQuestions, setShowQuestions] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>(opts.initialAnswers ?? DEFAULT_ANSWERS);
  const [extraInfo, setExtraInfo] = useState('');
  const [draft, setDraft] = useState<GenerateSuccess | null>(null);
  const [error, setError] = useState<GenerateError | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Optional questions for the current role. Re-runs whenever the host form
  // changes the role (e.g. fulltime to temp); `stepIndex` is clamped below so
  // it never lands past the shorter temp variant.
  const questions = useMemo(
    () => optionalQuestionsForPositionType(opts.positionType),
    [opts.positionType]
  );
  const totalSteps = questions.length;

  useEffect(() => {
    setStepIndex(i => Math.min(i, Math.max(totalSteps - 1, 0)));
  }, [totalSteps]);

  const currentQuestion = questions[Math.min(stepIndex, totalSteps - 1)];

  const setAnswer = useCallback(<K extends keyof Answers>(key: K, value: Answers[K]) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  }, []);

  // Every answer is optional, so the user can always continue or submit.
  const canContinue = true;
  const canSubmit = true;

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setPhase('wizard');
    setShowQuestions(false);
    setStepIndex(0);
    setAnswers(opts.initialAnswers ?? DEFAULT_ANSWERS);
    setExtraInfo('');
    setDraft(null);
    setError(null);
  }, [opts.initialAnswers]);

  const openQuestions = useCallback(() => {
    setStepIndex(0);
    setShowQuestions(true);
  }, []);

  const next = useCallback(() => {
    setStepIndex(i => Math.min(i + 1, totalSteps - 1));
  }, [totalSteps]);

  // Back from the first question returns to the first screen.
  const back = useCallback(() => {
    if (stepIndex === 0) {
      setShowQuestions(false);
      return;
    }
    setStepIndex(i => Math.max(i - 1, 0));
  }, [stepIndex]);

  const submit = useCallback(
    async (overrides: Partial<GenerateRequest> = {}) => {
      if (!opts.practiceId || !opts.jobTypeId) {
        setError({
          success: false,
          error: 'Choose a practice and job type first.',
          code: 'invalid_input',
        });
        setPhase('error');
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setPhase('loading');
      setError(null);

      const payload: GenerateRequest = {
        ...(opts.jobFields ?? {}),
        practice_id: opts.practiceId,
        job_type_id: Number(opts.jobTypeId),
        position_type: opts.positionType,
        skill_ids: opts.skillIds,
        years_experience: opts.yearsExperience,
        answers,
        extra_info: extraInfo.trim() || undefined,
        ...overrides,
      };

      // When the user is regenerating an existing draft we want to keep the
      // current preview visible if the regen fails (rate-limit, AI hiccup,
      // etc.) so they don't lose context. Plain submissions go to error.
      const regenerating = Boolean(overrides.draft_id);

      try {
        const result = await generateJobDescription(payload, {
          signal: controller.signal,
          endpointBase: opts.endpointBase,
        });
        if (result.success) {
          setDraft(result);
          setPhase('preview');
        } else {
          setError(result);
          setPhase(regenerating ? 'preview' : 'error');
        }
      } catch (e) {
        if ((e as { name?: string }).name === 'AbortError') return;
        setError({
          success: false,
          error: 'Something went wrong generating the description. Please try again.',
          code: 'unknown',
        });
        setPhase(regenerating ? 'preview' : 'error');
      }
    },
    [opts, answers, extraInfo]
  );

  const regenerate = useCallback(
    (hint?: string) => {
      if (!draft) return;
      return submit({ draft_id: draft.draft_id, regenerate_hint: hint });
    },
    [draft, submit]
  );

  const dismissError = useCallback(() => setError(null), []);

  return {
    phase,
    showQuestions,
    stepIndex,
    totalSteps,
    currentQuestion,
    questions,
    answers,
    extraInfo,
    draft,
    error,
    canContinue,
    canSubmit,
    setAnswer,
    setExtraInfo,
    openQuestions,
    next,
    back,
    submit,
    regenerate,
    reset,
    setPhase,
    dismissError,
  };
}

// Re-exports for convenience in components and tests.
export type { Tone, BenefitKey };
